import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Html5Qrcode } from "html5-qrcode";
import { formatLojaName } from "../utils/lodgeName";
import {
  QrCode,
  Printer,
  Camera,
  CameraOff,
  CheckCircle2,
  Users,
  RefreshCw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AttendanceQRView = ({ sessions = [] }) => {
  const [subTab, setSubTab] = useState("scan"); // scan | print
  const [selectedSessionId, setSelectedSessionId] = useState("");

  const openSessions = useMemo(() => {
    return (sessions || []).slice().sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [sessions]);

  useEffect(() => {
    if (!selectedSessionId && openSessions.length > 0) {
      setSelectedSessionId(openSessions[0].id);
    }
  }, [openSessions, selectedSessionId]);

  return (
    <div className="space-y-6" data-testid="attendance-qr-view">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#D4AF37] font-bold mb-1">
          <QrCode className="w-4 h-4" />
          <span>Chancelaria • Presença por QR</span>
        </div>
        <h2 className="text-2xl font-serif font-bold text-white">Check-in por QR Code</h2>
        <p className="text-xs text-slate-400 mt-1">
          Cada Irmão tem um QR Code único. Imprima a lista uma vez, entregue as carteirinhas —
          e na porta do Templo basta apontar a câmera do celular pra marcar presença automaticamente.
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex gap-2">
          <button
            data-testid="attendance-tab-scan"
            onClick={() => setSubTab("scan")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs uppercase tracking-widest font-bold transition-all ${
              subTab === "scan"
                ? "bg-[#D4AF37] text-slate-950 shadow-[0_0_15px_rgba(212,175,55,0.25)]"
                : "bg-slate-800 text-slate-300 hover:text-white"
            }`}
          >
            <Camera className="w-3.5 h-3.5" />
            Escanear Presença
          </button>
          <button
            data-testid="attendance-tab-print"
            onClick={() => setSubTab("print")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs uppercase tracking-widest font-bold transition-all ${
              subTab === "print"
                ? "bg-[#D4AF37] text-slate-950 shadow-[0_0_15px_rgba(212,175,55,0.25)]"
                : "bg-slate-800 text-slate-300 hover:text-white"
            }`}
          >
            <Printer className="w-3.5 h-3.5" />
            Imprimir Lista com QR
          </button>
        </div>

        {subTab === "scan" && (
          <div className="flex-1 md:ml-auto">
            <label className="text-[10px] uppercase text-slate-400 tracking-widest font-bold">Sessão ativa</label>
            <select
              data-testid="attendance-session-select"
              value={selectedSessionId}
              onChange={(e) => setSelectedSessionId(e.target.value)}
              className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
            >
              <option value="">Selecione a sessão…</option>
              {openSessions.map((s) => (
                <option key={s.id} value={s.id}>
                  Sessão nº {s.session_number} — {s.title} ({s.date})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {subTab === "scan" && <ScanTab sessionId={selectedSessionId} />}
      {subTab === "print" && <PrintTab />}
    </div>
  );
};

// ==========================================
// SCAN TAB — usa html5-qrcode (funciona no navegador do celular)
// ==========================================

const ScanTab = ({ sessionId }) => {
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const lastTokenRef = useRef({ token: null, ts: 0 });

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (e) {
        // ignore
      }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    if (!sessionId) {
      toast.error("Selecione a sessão antes de iniciar a câmera.");
      return;
    }
    try {
      const inst = new Html5Qrcode("qr-scanner-region");
      scannerRef.current = inst;
      await inst.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        async (decodedText) => {
          const now = Date.now();
          // debounce mesma leitura em 3s
          if (lastTokenRef.current.token === decodedText && now - lastTokenRef.current.ts < 3000) return;
          lastTokenRef.current = { token: decodedText, ts: now };
          await handleScan(decodedText);
        },
        () => {}
      );
      setScanning(true);
    } catch (e) {
      toast.error("Não foi possível acessar a câmera. Autorize o navegador e tente novamente.");
      scannerRef.current = null;
    }
  };

  const handleScan = async (rawText) => {
    try {
      // aceita tanto o token puro quanto uma URL contendo ?t=token
      let token = rawText.trim();
      const match = token.match(/[?&]t=([^&]+)/);
      if (match) token = decodeURIComponent(match[1]);

      const res = await axios.post(`${API}/attendance/checkin`, {
        qr_token: token,
        session_id: sessionId,
      });
      setLastResult(res.data);
      const already = res.data.already_registered;
      const name = res.data.member?.name || "Irmão";
      if (already) {
        toast.info(`${name} já estava presente.`);
      } else {
        toast.success(`✓ ${name} marcado como presente!`);
        setCheckins((prev) => [
          {
            id: res.data.member.id,
            name,
            time: new Date().toLocaleTimeString("pt-BR"),
            cim: res.data.member?.cim,
          },
          ...prev.filter((c) => c.id !== res.data.member.id),
        ]);
        // beep
        try {
          const audio = new AudioContext();
          const osc = audio.createOscillator();
          osc.frequency.value = 880;
          osc.connect(audio.destination);
          osc.start();
          setTimeout(() => { osc.stop(); audio.close(); }, 120);
        } catch (e) {
          // sem áudio
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "QR não reconhecido.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="luxury-card p-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            <div id="qr-scanner-region" data-testid="attendance-scanner-region" className="rounded-2xl overflow-hidden bg-black min-h-[280px]" />
            <div className="mt-3 flex gap-2 justify-center">
              {!scanning ? (
                <button
                  data-testid="attendance-btn-start"
                  onClick={startScanner}
                  className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-6 py-3 rounded-xl text-xs uppercase tracking-widest transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)]"
                >
                  <Camera className="w-4 h-4" />
                  Iniciar câmera
                </button>
              ) : (
                <button
                  data-testid="attendance-btn-stop"
                  onClick={stopScanner}
                  className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white font-bold px-6 py-3 rounded-xl text-xs uppercase tracking-widest transition-all"
                >
                  <CameraOff className="w-4 h-4" />
                  Parar
                </button>
              )}
            </div>
            <div className="mt-3 text-center text-[11px] text-slate-500 italic">
              Aponte a câmera para o QR do Irmão. Bipe = presença marcada.
            </div>
          </div>

          <div className="md:w-72">
            <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2 flex items-center gap-2">
              <Users className="w-3 h-3" />
              Marcados nesta sessão
              <span
                data-testid="attendance-count"
                className="ml-auto text-lg font-serif font-bold text-[#D4AF37]"
              >
                {lastResult?.present_count ?? checkins.length}
              </span>
            </div>
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
              {checkins.length === 0 && (
                <div className="text-[10px] text-slate-500 italic">Nenhum check-in nesta sessão da câmera.</div>
              )}
              {checkins.map((c) => (
                <div
                  key={c.id}
                  data-testid={`attendance-checkin-${c.id}`}
                  className="flex items-center justify-between gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs text-white font-semibold truncate">{c.name}</div>
                      <div className="text-[9px] text-slate-400 font-mono">CIM {c.cim}</div>
                    </div>
                  </div>
                  <div className="text-[9px] text-slate-400 font-mono">{c.time}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// PRINT TAB — folha A4 com QR de cada Irmão
// ==========================================

const PrintTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState({});

  const fetchRoster = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/attendance/roster-print`);
      setData(res.data);
    } catch (e) {
      toast.error("Erro ao carregar roster.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoster();
  }, []);

  const regenerate = async (id) => {
    if (!window.confirm("Regenerar o QR deste Irmão invalida a carteirinha antiga. Confirma?")) return;
    setRegenerating((r) => ({ ...r, [id]: true }));
    try {
      await axios.post(`${API}/members/${id}/regenerate-qr`);
      await fetchRoster();
      toast.success("QR regenerado.");
    } catch (e) {
      toast.error("Erro ao regenerar QR.");
    } finally {
      setRegenerating((r) => ({ ...r, [id]: false }));
    }
  };

  if (loading) {
    return <div className="text-center text-slate-400 text-xs py-6">Carregando…</div>;
  }
  if (!data?.members || data.members.length === 0) {
    return <div className="text-center text-slate-500 text-xs italic py-8">Nenhum Irmão cadastrado.</div>;
  }

  return (
    <div id="attendance-print-root" className="space-y-4">
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          html, body { background: #fff !important; color: #000 !important; }
          body * { visibility: hidden !important; }
          #attendance-print-root, #attendance-print-root * { visibility: visible !important; }
          #attendance-print-root { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; background: #fff !important; color: #000 !important; }
          #attendance-print-root .print-hidden { display: none !important; }
          #attendance-print-root .qr-card { background: #fff !important; border: 1.5px solid #333 !important; color: #000 !important; page-break-inside: avoid !important; }
          #attendance-print-root .qr-card .name { color: #000 !important; }
          #attendance-print-root .qr-card .subtitle, #attendance-print-root .qr-card .footer { color: #444 !important; }
        }
      `}</style>

      <div className="flex justify-between items-center print-hidden">
        <div className="text-xs text-slate-400">
          {data.members.length} Irmãos • Loja {formatLojaName(data.loja || {})}
        </div>
        <button
          data-testid="attendance-btn-print"
          onClick={() => window.print()}
          className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-2 rounded-xl text-xs uppercase tracking-widest shadow-[0_0_15px_rgba(212,175,55,0.25)]"
        >
          <Printer className="w-4 h-4" />
          Imprimir folha A4
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.members.map((m) => (
          <div
            key={m.id}
            data-testid={`qr-card-${m.id}`}
            className="qr-card bg-white text-slate-900 rounded-xl border-2 border-slate-800 p-4 flex items-center gap-4 shadow"
            style={{ fontFamily: "'Georgia','Times New Roman',serif" }}
          >
            <div className="flex-shrink-0">
              <QRCodeSVG value={m.qr_token} size={104} level="M" includeMargin={false} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 subtitle">
                CIM {m.cim || "—"}
              </div>
              <div className="name text-sm font-bold text-slate-900 leading-tight break-words">{m.name}</div>
              <div className="subtitle text-[10px] text-slate-600 mt-0.5">{m.degree}</div>
              <div className="subtitle text-[10px] text-slate-600 italic">{m.office}</div>
              <div className="footer text-[9px] text-slate-500 mt-1 font-mono">{m.qr_token}</div>
              <button
                onClick={() => regenerate(m.id)}
                disabled={regenerating[m.id]}
                className="print-hidden mt-1 text-[9px] text-slate-500 hover:text-rose-600 flex items-center gap-1"
              >
                <RefreshCw className="w-2.5 h-2.5" /> Regenerar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
